"use client";

/**
 * SearchPerformanceLive — live query-speed metrics for the Search engine,
 * pulled from the public /search/perf endpoint (refreshed every 15s).
 *
 * Shows the real pipeline payoff: 1M+ pages indexed, sub-150ms cold queries,
 * ~1ms cached, GPU-accelerated embedding. Degrades gracefully to "—" if the
 * endpoint is briefly unreachable.
 */
import { useEffect, useState } from "react";
import { Gauge, Zap, Cpu, Repeat } from "lucide-react";
import { fmtCount, fmtMs, fmtPct } from "@/lib/format-stat";

type Perf = {
  queries: number;
  cache_hit_rate: number;
  avg_cold_ms: number;
  avg_cached_ms: number;
  gpu_embed_share: number;
  pages_indexed: number;
};

const PERF_URL = "https://extension.askfinz.ai/api/v1/search/perf";

export function SearchPerformanceLive({ initial }: { initial?: Perf | null }) {
  /* Seeded by the server (see live-stats.server.tsx) so the figure is in the HTML on first
     paint rather than replacing a dash after hydration. The client fetch below stays: the page
     is prerendered on a 5-minute timer, and this is what keeps a cached copy current. */
  const [p, setP] = useState<Perf | null>(initial ?? null);
  const [live, setLive] = useState(false);

  useEffect(() => {
    let on = true;
    const load = async () => {
      try {
        const r = await fetch(PERF_URL, { cache: "no-store" });
        if (!r.ok) return;
        const d = (await r.json()) as Perf;
        if (on) {
          setP(d);
          setLive(true);
        }
      } catch {
        /* transient — keep last values */
      }
    };
    load();
    const t = setInterval(load, 15000);
    return () => {
      on = false;
      clearInterval(t);
    };
  }, []);

  // Local formatters replaced by the shared ones: `num` printed 1,440,377 in full and `ms`
  // rendered a sub-millisecond cache hit as "0 ms", which reads as broken rather than fast.
  const ms = fmtMs;

  const stats = [
    // "Pages indexed" removed while the index is being built — a running total invites the one
    // comparison we lose, and freezes into every cached copy and AI summary of this page. The rest
    // of this block is latency and hit-rate, which are true at any size and are what a performance
    // panel is actually for.
    { icon: Gauge, value: p ? ms(p.avg_cold_ms) : "—", label: "Cold query", sub: "fresh, never seen before" },
    { icon: Zap, value: p ? ms(p.avg_cached_ms) : "—", label: "Cached query", sub: "popular / repeat" },
    { icon: Repeat, value: p ? fmtPct(p.cache_hit_rate) : "—", label: "Cache hit rate", sub: "served without re-search" },
    { icon: Cpu, value: p ? fmtPct(p.gpu_embed_share) : "—", label: "GPU-embedded", sub: "with CPU fallback" },
  ];

  return (
    <div>
      <div className="flex items-center gap-2 mb-5">
        <span
          className={`inline-block h-2 w-2 rounded-full ${live ? "bg-emerald-500 animate-pulse" : "bg-neutral-400"}`}
          aria-hidden
        />
        <span className="kicker">
          {live ? "Live" : "Connecting…"}
          {p ? ` · ${fmtCount(p.queries)} queries served` : ""}
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {stats.map((s) => (
          <div key={s.label} className="lift-card rounded-xl p-4">
            <s.icon className="h-4 w-4 opacity-50 mb-3" aria-hidden />
            <div className="text-2xl md:text-3xl font-semibold tabular-nums leading-none">{s.value}</div>
            <div className="text-sm mt-2 opacity-80">{s.label}</div>
            <div className="text-xs mt-0.5 opacity-50">{s.sub}</div>
          </div>
        ))}
      </div>
      <p className="text-xs opacity-50 mt-4 font-mono">
        query&nbsp;→&nbsp;vector on the GPU pool · payload-indexed SafeSearch filter · deterministic result cache
      </p>
    </div>
  );
}
